import React, { memo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import { Info as InfoIcon, ShoppingCart as ShoppingCartIcon } from '@mui/icons-material';

/**
 * ItemCard Component - Displays a Splinterlands item in the market
 * Styled to match CardItem on the cards market page (src/scenes/market/CardItem.jsx).
 *
 * @param {Object} props - Component props
 * @param {Object} props.item - The item data from the market landing API
 * @param {Function} props.onItemClick - Callback when item is clicked
 * @param {Function} props.onBuyClick - Callback when buy button is clicked
 * @returns {JSX.Element} - The rendered item card component
 */
const ItemCard = memo(({ item, onItemClick, onBuyClick }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Reset image states when item changes
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [item?.detailImage]);

  if (!item) return null;

  // Get the asset type for styling
  const getAssetTypeColor = () => {
    switch (item.assetName) {
      case 'PACKS':
        return '#4CAF50'; // Green
      case 'LAND':
        return '#8BC34A'; // Light Green
      case 'TOTEMS':
        return '#FF9800'; // Orange
      case 'TITLES':
        return '#9C27B0'; // Purple
      case 'DEEDS':
        return '#795548'; // Brown
      case 'LAND_RESOURCES':
        return '#00BCD4'; // Cyan
      case 'TOTEM_ITEMS':
        return '#FF5722'; // Deep Orange
      case 'TOTEM_FRAGMENTS':
        return '#FFC107'; // Amber
      case 'AVATARS':
        return '#E91E63'; // Pink
      case 'CONSUMABLES':
        return '#3F51B5'; // Indigo
      case 'SKINS':
        return '#F44336'; // Red
      case 'COLLECTOR_STICKERS':
        return '#009688'; // Teal
      case 'MUSIC':
        return '#673AB7'; // Deep Purple
      default:
        return colors.grey[400];
    }
  };

  const assetTypeColor = getAssetTypeColor();

  // Format price
  const formatPrice = (price) => {
    if (price >= 1000) {
      return `$${(price / 1000).toFixed(1)}k`;
    }
    return price < 1 ? `$${price.toFixed(3)}` : `$${price.toFixed(2)}`;
  };

  const minPrice = item.prices && item.prices.length > 0 ? item.prices[0].minPrice : 0;
  const hasListings = item.numListed > 0;

  return (
    <Box
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onItemClick && onItemClick(item)}
      sx={{
        backgroundColor: colors.primary[400],
        transition: "transform 0.2s, box-shadow 0.3s",
        cursor: 'pointer',
        height: "100%",
        display: "flex",
        flexDirection: "column",
        boxShadow: isHovered ? `0 8px 16px rgba(0,0,0,0.3)` : "0 4px 6px rgba(0,0,0,0.1)",
        transform: isHovered ? "translateY(-8px)" : "none",
        position: "relative",
        borderRadius: "12px",
        overflow: "hidden",
      }}
    >
      {/* Asset Type Indicator (same corner tab as the foil indicator on cards) */}
      {item.assetDescription && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            backgroundColor: assetTypeColor,
            color: "#FFF",
            padding: "2px 8px",
            borderBottomLeftRadius: "8px",
            fontSize: { xs: "0.6rem", sm: "0.7rem" },
            fontWeight: "bold",
            textTransform: "uppercase",
            zIndex: 2,
          }}
        >
          {item.assetDescription}
        </Box>
      )}

      {/* Info Button */}
      {item.detailDescription && (
        <Tooltip title={item.detailDescription} arrow placement="top">
          <IconButton
            size="small"
            onClick={(e) => e.stopPropagation()}
            sx={{
              color: colors.grey[300],
              position: 'absolute',
              top: 4,
              left: 4,
              zIndex: 2,
              backgroundColor: `${colors.grey[900]}80`,
              width: 24,
              height: 24,
              '&:hover': {
                color: colors.grey[100],
                backgroundColor: `${colors.grey[800]}90`
              }
            }}
          >
            <InfoIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}

      {/* Item Image */}
      <Box
        sx={{
          pt: { xs: 0.5, sm: 2 },
          px: { xs: 0.5, sm: 2 },
          position: "relative",
          flex: "1 0 auto",
          display: "flex",
          justifyContent: "center"
        }}
      >
        <Box
          sx={{
            height: { xs: 140, sm: 280 },
            width: "100%",
            position: "relative",
            borderRadius: "10px",
            overflow: "hidden",
            boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
            border: `2px solid ${colors.primary[400]}`,
            backgroundColor: colors.primary[500],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: "transform 0.3s",
            transform: isHovered ? "scale(1.05)" : "scale(1)"
          }}
        >
          {item.detailImage && !imageError ? (
            <img
              src={item.detailImage}
              alt={item.detailName}
              loading="lazy"
              style={{
                maxWidth: '85%',
                maxHeight: '85%',
                objectFit: 'contain',
                opacity: imageLoaded ? 1 : 0.7,
                transition: 'opacity 0.3s ease',
                filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.4))'
              }}
              onLoad={() => {
                setImageLoaded(true);
                setImageError(false);
              }}
              onError={() => {
                console.warn(`Failed to load image for ${item.detailName}:`, item.detailImage);
                setImageError(true);
                setImageLoaded(false);
              }}
            />
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: assetTypeColor,
                fontWeight: 'bold',
                textAlign: 'center',
                fontSize: '0.8rem',
                px: 1
              }}
            >
              {item.detailName || item.assetName}
              {imageError && (
                <Box component="span" sx={{ display: 'block', color: colors.grey[400], fontSize: '0.7rem', fontWeight: 'normal' }}>
                  Image unavailable
                </Box>
              )}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Item Info */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', pt: { xs: 0.25, sm: 1 }, px: { xs: 0.5, sm: 2 }, pb: { xs: 0.5, sm: 2 } }}>
        <Typography
          variant="body1"
          fontWeight="bold"
          title={item.detailName}
          sx={{
            textAlign: "center",
            mb: { xs: 0.25, sm: 1 },
            fontSize: { xs: '0.65rem', sm: '1rem' },
            lineHeight: { xs: 1.2, sm: 1.4 },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            minHeight: { xs: '1.5rem', sm: '2.8rem' }
          }}
        >
          {item.detailName}
        </Typography>

        {/* Price + listed count (same layout as price + price/BCX on cards) */}
        <Box
          display="flex"
          flexDirection="row"
          alignItems="baseline"
          justifyContent="center"
          gap={{ xs: 0.75, sm: 1.5 }}
        >
          <Tooltip title="Lowest listing price (USD)">
            <Typography
              variant="body2"
              sx={{
                color: minPrice > 0 ? colors.greenAccent[400] : colors.grey[500],
                fontSize: { xs: '0.85rem', sm: '0.95rem' },
              }}
            >
              {minPrice > 0 ? formatPrice(minPrice) : 'N/A'}
            </Typography>
          </Tooltip>
          <Tooltip title={`Circulation: ${item.numCirculation?.toLocaleString() || 'N/A'}`}>
            <Typography
              variant="caption"
              sx={{
                color: colors.blueAccent[400],
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                whiteSpace: 'nowrap',
              }}
            >
              {(item.numListed || 0).toLocaleString()} listed
            </Typography>
          </Tooltip>
        </Box>

        {/* Buy Button */}
        <Button
          fullWidth
          size="small"
          variant="outlined"
          startIcon={<ShoppingCartIcon sx={{ fontSize: '1rem !important' }} />}
          onClick={(e) => {
            e.stopPropagation();
            onBuyClick && onBuyClick(item);
          }}
          disabled={!hasListings}
          sx={{
            mt: { xs: 0.75, sm: 1.5 },
            color: colors.greenAccent[400],
            borderColor: `${colors.greenAccent[600]}80`,
            fontWeight: 'bold',
            borderRadius: 2,
            textTransform: 'none',
            fontSize: { xs: '0.7rem', sm: '0.8rem' },
            py: { xs: 0.25, sm: 0.5 },
            transition: 'all 0.2s',
            ...(isHovered && hasListings && {
              backgroundColor: colors.greenAccent[600],
              borderColor: colors.greenAccent[600],
              color: '#FFF'
            }),
            '&:hover': {
              backgroundColor: colors.greenAccent[700],
              borderColor: colors.greenAccent[700],
              color: '#FFF'
            },
            '&:disabled': {
              borderColor: colors.grey[700],
              color: colors.grey[500]
            }
          }}
        >
          {hasListings ? 'View Listings' : 'Sold Out'}
        </Button>
      </Box>
    </Box>
  );
});

export default ItemCard;
